import type { RouteLocationNormalizedGeneric } from '#vue-router';
import type { NuxtApp } from '#app';
import { appPageTransition as defaultPageTransition } from '#build/nuxt.config.mjs';

export default defineNuxtPlugin({
  name: 'page-loading',
  dependsOn: ['nuxt:router'],
  parallel: true,
  setup(_nuxtApp) {
    const nuxtApp = _nuxtApp as NuxtApp;
    const { $router, $pinia } = _nuxtApp as NuxtApp;

    const mainStore = useMainStore($pinia);

    let isOnErrorPage = false;

    onNuxtReady(() => {
      logger.debug('nuxtReady');
      document.body.classList.add('nuxt-ready');
      document.body.dispatchEvent(createCustomEvent('nuxtReady'));
    });

    // Called when nuxt page component is resolved:
    // Show the loading spinner as early as possible
    nuxtApp.hook('page:start', () => {
      logger.debug('page:start', { isHydrating: nuxtApp.isHydrating });

      if (nuxtApp.isHydrating) {
        return;
      }

      mainStore.startLoading();
    });

    // Called when new nuxt page setup is running:
    // Show the loading spinner too, for good measure
    nuxtApp.hook('page:loading:start', () => {
      logger.debug('page:loading:start', { isHydrating: nuxtApp.isHydrating });
      window.dispatchEvent(new CustomEvent('page:loading:start'));

      if (nuxtApp.isHydrating) {
        return;
      }

      mainStore.startLoading();
    });

    // This hook is called once for the page component which has fully finished loading + rendering.
    // Note: This hook is only called once as it should be, so no need for "firstLoad" state
    nuxtApp.hook('page:finish', () => {
      logger.debug('page:finish');
      window.dispatchEvent(new CustomEvent('page:finish'));

      // When we show the error page instead of the target route/page, and it would use page transitions,
      // we instead need to use the normal `page:finish` event since the transition one doesn't fire
      if (useError().value) {
        isOnErrorPage = true;
      }
    });

    // Called after `page:finish`, but currently called twice during navigation (hence the `firstLoad` state)
    // @see https://github.com/nuxt/nuxt/issues/26535
    nuxtApp.hook('page:loading:end', () => {
      logger.debug('page:loading:end', { firstLoad: mainStore.firstLoad });
      window.dispatchEvent(new CustomEvent('page:loading:end', { detail: { firstLoad: mainStore.firstLoad } }));
      mainStore.firstLoad = false;
    });

    // Called after `page:loading:end`, and only when the transition was fully finished
    nuxtApp.hook('page:transition:finish', () => {
      logger.debug('page:transition:finish');
      window.dispatchEvent(new CustomEvent('page:transition:finish'));
    });

    $router.afterEach(() => {
      logger.debug('nuxtRouteChange', { isHydrating: nuxtApp.isHydrating });

      if (nuxtApp.isHydrating) {
        return;
      }

      document.body.dispatchEvent(createCustomEvent('nuxtRouteChange'));
    });

    const router = useRouter();
    const hasTransition = (route: RouteLocationNormalizedGeneric) =>
      !!route.meta.pageTransition || defaultPageTransition;

    // Called after page setup, including possible page transition
    router.beforeEach((to, from) => {
      // Nuxt does not have a `layout:transition:finish` hook,
      // and `page:transition:finish` does not trigger during layout changes.
      // @see https://github.com/nuxt/nuxt/issues/29004
      const hasLayoutChange = to.meta.layout !== from.meta.layout;
      const hasPageComponentChange = to.meta.name !== from.meta.name;
      const hasPageTransition = hasTransition(to) && hasTransition(from);

      // Determine hook that should set finishLoading state
      // Error page is a special case, it is a root-level vue component instead of a nuxt page.
      // Cases which must use `transition:finish` might flicker if they use `loading:end` instead (see checkout steps).
      const hookToWait =
        !isOnErrorPage && !hasLayoutChange && hasPageComponentChange && hasPageTransition
          ? 'page:transition:finish'
          : 'page:loading:end';

      const unregisterHook = nuxtApp.hook(hookToWait as any, () => {
        mainStore.finishLoading();
        logger.debug('finishLoadingHook:after', { hookToWait });

        // Do NOT just set this to false, always use the current error value.
        // We handle navigation from error-page to error-page this way.
        isOnErrorPage = !!useError().value;

        unregisterHook();
      });

      nuxtApp.hook('vue:error' as any, (error: any) => {
        mainStore.finishLoading();
        logger.debug('finishLoadingHook:after', { hookToWait: 'vue:error' });

        /**
         * The `page:loading:end` hook is only triggered on the client side.
         * It is not fired when the error page is displayed, so we manually
         * dispatch it for tracking purposes. The same applies to our custom
         * window event if an vue:error occurs, `page:loading:end` must be
         * dispatched manually.
         */
        if (mainStore.firstLoad && hookToWait === 'page:loading:end') {
          const pageData = {
            ...useDatalayerStore().$state,
            error,
          };

          addToEventQueue('page:loading:end', {
            firstLoad: mainStore.firstLoad,
            location: window?.location,
            pageData: toRaw(pageData),
          });

          mainStore.firstLoad = false;
          window.dispatchEvent(new CustomEvent('page:loading:end'));

          // On error, set pageData.error manually, since nuxtApp.payload?.error
          // may be undefined in some cases and the error could be lost.
          // For reference, please see 15.datalayer.global.ts errorValue handling
          window.pageData = {
            ...window?.pageData,
            error,
          };
        }
      });
    });
  },
});
